import { XCircle, Copy01, File02, AlertTriangle } from '@untitledui/icons';
import { ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { useTranslation } from 'react-i18next';
import type { DetectedDocumentType } from '@/types/api/document-training';

interface SplitDocumentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
    documentTypes: DetectedDocumentType[];
    fileName?: string;
}

export const SplitDocumentModal = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    documentTypes,
    fileName,
}: SplitDocumentModalProps) => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);

    const getConfidenceBadgeColor = (confidence: number) => {
        if (confidence >= 0.9) return 'success';
        if (confidence >= 0.7) return 'warning';
        return 'error';
    };

    const formatPageRange = (docType: DetectedDocumentType) => {
        if (docType.page_start === docType.page_end) {
            return t('training.split.pageRangeSingle', {
                defaultValue: 'Page {{page}}',
                page: docType.page_start,
            });
        }
        return t('training.split.pageRangeMultiple', {
            defaultValue: 'Pages {{start}}-{{end}}',
            start: docType.page_start,
            end: docType.page_end,
        });
    };

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal className="max-w-lg">
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning-subtle">
                                    <Copy01 className="size-5 text-warning" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-primary">
                                        {t('training.split.modal.title', { defaultValue: 'Split Multi-Document PDF' })}
                                    </h2>
                                    <p className="text-sm text-tertiary">
                                        {t('training.split.modal.subtitle', {
                                            defaultValue: '{{count}} document types detected',
                                            count: documentTypes.length,
                                        })}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-tertiary hover:text-primary"
                                aria-label={t('common:buttons.close')}
                                disabled={isLoading}
                            >
                                <XCircle className="size-5" />
                            </button>
                        </div>

                        {/* File Name */}
                        {fileName && (
                            <div className="mb-4 p-3 rounded-lg bg-secondary">
                                <div className="flex items-center gap-2 text-sm text-tertiary">
                                    <File02 className="size-4" />
                                    <span className="font-medium">{fileName}</span>
                                </div>
                            </div>
                        )}

                        {/* Warning Message */}
                        <div className="mb-4 p-3 rounded-lg bg-warning-subtle border border-warning-subtle">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="size-4 text-warning mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-warning">
                                    {t('training.split.modal.warning', {
                                        defaultValue: 'This will create {{count}} separate training documents from this PDF. The original document will be marked as a parent.',
                                        count: documentTypes.length,
                                    })}
                                </p>
                            </div>
                        </div>

                        {/* Document Types List */}
                        <div className="mb-6 space-y-2">
                            <label className="text-sm font-medium text-tertiary">
                                {t('training.split.modal.detectedTypes', { defaultValue: 'Detected Document Types' })}
                            </label>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {documentTypes.map((docType, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 rounded-lg bg-secondary hover:bg-tertiary transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-subtle text-brand font-medium text-sm">
                                                {index + 1}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-primary">
                                                    {docType.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                </p>
                                                <p className="text-xs text-tertiary">
                                                    {formatPageRange(docType)}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge
                                            color={getConfidenceBadgeColor(docType.confidence)}
                                            size="sm"
                                        >
                                            {(docType.confidence * 100).toFixed(0)}%
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            <Button
                                onClick={onClose}
                                color="secondary"
                                size="md"
                                isDisabled={isLoading}
                            >
                                {t('common:buttons.cancel')}
                            </Button>
                            <Button
                                onClick={onConfirm}
                                color="primary"
                                size="md"
                                iconLeading={Copy01}
                                isDisabled={isLoading || documentTypes.length === 0}
                            >
                                {isLoading
                                    ? t('training.split.modal.splitting', { defaultValue: 'Splitting...' })
                                    : t('training.split.modal.confirmSplit', {
                                        defaultValue: 'Split into {{count}} Documents',
                                        count: documentTypes.length,
                                    })}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
